// Mapa de trazabilidad de una herramienta — /bodega/herramientas/:codigo/historial
// Timeline vertical con eventos: creación, asignaciones, transferencias, scans, impresiones.
import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  ArrowLeft, Search, Loader2, Database, Sparkles, UserPlus, UserMinus,
  Undo2, Truck, CheckCircle2, AlertTriangle, Check, XCircle, Printer,
  History, Activity, Package, Tag, MapPin,
} from 'lucide-react';
import { toast } from 'sonner';

const ICONS = {
  database: Database, sparkles: Sparkles, 'user-plus': UserPlus,
  'user-minus': UserMinus, undo: Undo2, truck: Truck,
  'check-circle-2': CheckCircle2, 'alert-triangle': AlertTriangle,
  check: Check, 'x-circle': XCircle, printer: Printer, history: History,
  scan: Activity,
};

const COLOR_CLASSES = {
  emerald: { dot: 'bg-emerald-500', border: 'border-emerald-300', text: 'text-emerald-700' },
  indigo:  { dot: 'bg-indigo-500',  border: 'border-indigo-300',  text: 'text-indigo-700' },
  amber:   { dot: 'bg-amber-500',   border: 'border-amber-300',   text: 'text-amber-700' },
  rose:    { dot: 'bg-rose-500',    border: 'border-rose-300',    text: 'text-rose-700' },
  slate:   { dot: 'bg-slate-400',   border: 'border-slate-300',   text: 'text-slate-700' },
};

function formatFecha(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-CL', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso.slice(0, 16).replace('T', ' '); }
}

export default function HerramientaTimelinePage() {
  const { codigo: codigoParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [input, setInput] = useState(codigoParam || searchParams.get('q') || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const buscar = async (c) => {
    const codigo = (c || input).trim();
    if (!codigo) return;
    setLoading(true); setError('');
    try {
      const res = await api.get(`/bodega/herramientas/${encodeURIComponent(codigo)}/timeline`);
      setData(res.data);
      setSearchParams({ q: codigo });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Error cargando timeline');
      setData(null);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const q = codigoParam || searchParams.get('q');
    if (q) buscar(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codigoParam]);

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-5" data-testid="timeline-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Volver
        </Button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MapPin className="w-6 h-6 text-indigo-600" /> Mapa de Herramienta
          </h1>
          <p className="text-sm text-slate-500">Trazabilidad cronológica por código.</p>
        </div>
      </div>

      {/* Search bar */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={(e) => { e.preventDefault(); buscar(); }} className="flex gap-2">
            <Input
              placeholder="Código de herramienta (ej: 0102104801250000001)"
              value={input} onChange={(e) => setInput(e.target.value)}
              className="font-mono"
              data-testid="input-codigo"
            />
            <Button type="submit" disabled={loading || !input.trim()} data-testid="btn-buscar">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              <span className="ml-1">Buscar</span>
            </Button>
          </form>
        </CardContent>
      </Card>

      {error && (
        <Card className="border-rose-300" data-testid="error-card">
          <CardContent className="p-5 text-center space-y-2">
            <XCircle className="w-10 h-10 mx-auto text-rose-500" />
            <p className="text-rose-700">{error}</p>
          </CardContent>
        </Card>
      )}

      {!error && data && (
        <>
          {/* Header material */}
          <Card>
            <CardContent className="p-5 space-y-3">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-lg text-indigo-700" data-testid="codigo-header">
                    {data.codigo}
                  </div>
                  <div className="text-sm font-semibold text-slate-800">
                    {data.material.descripcion}
                  </div>
                  <div className="flex gap-2 mt-2">
                    {data.material.familia && (
                      <Badge variant="outline" className="text-xs">
                        <Tag className="w-3 h-3 mr-1" /> {data.material.familia}
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-xs">
                      {data.material.origen === 'access_import' ? 'Importada Access' : 'Nativa nube'}
                    </Badge>
                    {data.material.tipo_herramienta && (
                      <Badge className="text-xs bg-indigo-100 text-indigo-800 border-indigo-200">
                        Herramienta
                      </Badge>
                    )}
                  </div>
                </div>
                {data.estado_actual.asignado && (
                  <div className="text-right text-sm bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
                    <div className="text-xs text-slate-500">Ubicación actual</div>
                    <div className="font-semibold text-emerald-800">
                      {data.estado_actual.panol_nombre || '—'}
                    </div>
                    <div className="text-xs text-slate-500">
                      {data.estado_actual.asignado_a_nombre || '—'}
                    </div>
                  </div>
                )}
              </div>
              {/* Stats mini */}
              <div className="grid grid-cols-3 gap-2 pt-2 border-t">
                <div className="text-center">
                  <div className="text-2xl font-bold text-indigo-600">{data.stats.total_asignaciones}</div>
                  <div className="text-xs text-slate-500">Asignaciones</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-indigo-600">{data.stats.total_transferencias}</div>
                  <div className="text-xs text-slate-500">Transferencias</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-indigo-600">{data.stats.total_ciclos_inventario}</div>
                  <div className="text-xs text-slate-500">Ciclos inv.</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card>
            <CardContent className="p-5">
              <div className="text-sm font-semibold mb-4 flex items-center gap-2">
                <History className="w-4 h-4" /> Eventos ({data.total_eventos})
              </div>
              {data.events.length === 0 ? (
                <div className="text-slate-500 italic text-sm">Sin eventos registrados aún.</div>
              ) : (
                <ol className="relative border-l-2 border-slate-200 pl-6 space-y-4" data-testid="timeline-list">
                  {data.events.map((e, i) => {
                    const Icon = ICONS[e.icono] || History;
                    const c = COLOR_CLASSES[e.color] || COLOR_CLASSES.slate;
                    return (
                      <li key={i} className="relative" data-testid={`event-${i}`}>
                        <span className={`absolute -left-[34px] flex items-center justify-center w-6 h-6 rounded-full ${c.dot} ring-4 ring-white`}>
                          <Icon className="w-3 h-3 text-white" />
                        </span>
                        <div className={`bg-slate-50 border rounded-md p-3 ${c.border}`}>
                          <div className="flex items-start justify-between flex-wrap gap-2">
                            <div className="flex-1 min-w-0">
                              <div className={`text-sm font-semibold ${c.text}`}>
                                {e.titulo}
                              </div>
                              {e.detalle && (
                                <div className="text-xs text-slate-600 mt-0.5">{e.detalle}</div>
                              )}
                            </div>
                            <div className="text-[11px] text-slate-400 whitespace-nowrap">
                              {formatFecha(e.timestamp)}
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {!data && !error && !loading && !input && (
        <Card>
          <CardContent className="p-8 text-center text-slate-500">
            <Package className="w-12 h-12 mx-auto mb-2 opacity-40" />
            Ingresa el código de una herramienta para ver su historia.
          </CardContent>
        </Card>
      )}
    </div>
  );
}
